'use client'

import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { formatImageURL } from '@/utils/util'
import { navigateUrl } from '@/utils/client-request'
import { ArrowRight, Mail, MapPin, Phone, ShieldCheck, ShieldX, Sparkles } from 'lucide-react'
import { useNavigation } from '@/lib/commerce/hooks'
import ShopmnLogo from '@/assets/logo-white.svg'

const socialIcons: Record<string, (props: React.SVGProps<SVGSVGElement>) => React.ReactElement> = {
  facebook: (props) => (
    <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
      <path d="M9 8H6v4h3v12h5V12h3.6l.4-4H14V6.3c0-1 .2-1.3 1.2-1.3H18V0h-3.6C10.6 0 9 1.7 9 4.9V8z" />
    </svg>
  ),
  instagram: (props) => (
    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" {...props}>
      <rect x="2" y="2" width="20" height="20" rx="5" />
      <path d="M16 11.4a4 4 0 1 1-7.9 1.2 4 4 0 0 1 7.9-1.2zM17.5 6.5h.01" />
    </svg>
  ),
  twitter: (props) => (
    <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25h6.81l4.713 6.231 5.467-6.231z" />
    </svg>
  ),
  youtube: (props) => (
    <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
      <path d="M23 7s-.2-1.6-.9-2.3c-.8-.9-1.7-.9-2.1-1C16.7 3.5 12 3.5 12 3.5s-4.7 0-8 .2c-.4.1-1.3.1-2.1 1C1.2 5.4 1 7 1 7s-.2 1.9-.2 3.7v1.7c0 1.9.2 3.7.2 3.7s.2 1.6.9 2.3c.9.9 2 .9 2.5 1 1.8.2 7.6.2 7.6.2s4.7 0 8-.3c.5-.1 1.4-.1 2.2-1 .7-.7.9-2.3.9-2.3s.2-1.9.2-3.7v-1.7c0-1.9-.2-3.7-.2-3.7zM9.7 14.6V8.4l6.1 3.1-6.1 3.1z" />
    </svg>
  )
}

export default function Footer({ vendor, subscription }: { vendor?: any, subscription?: any }) {
  const pathname = usePathname()
  const fontClass = 'font-roboto'
  const { footer } = useNavigation({ vendor, subscription })
  const { info, help, socials, hasInfoSection } = footer
  const socialLinks = socials.map((s: any) => ({ ...s, icon: socialIcons[s.platform] })).filter((s: any) => s.icon)
  const name = vendor?.name || 'Shop.mn'

  return (
    <footer className={`${fontClass} mt-20`}>
      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pb-16">
        <div
          className="rounded-3xl p-10 sm:p-16 relative overflow-hidden text-center"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.035))',
            border: '1px solid rgba(255,255,255,0.13)',
          }}
        >
          <div
            className="absolute -right-20 -top-20 w-80 h-80 rounded-full opacity-15 blur-3xl pointer-events-none"
            style={{ background: 'linear-gradient(90deg,#e7e7ea 0%,#fafafa 100%)' }}
          />
          <div className="absolute -left-20 -bottom-20 w-80 h-80 rounded-full opacity-10 blur-3xl bg-white pointer-events-none" />
          <div className="relative max-w-2xl mx-auto">
            <div
              className="inline-flex items-center gap-2 h-7 px-3 rounded-full text-[10px] font-semibold uppercase tracking-[0.2em]"
              style={{ background: 'rgba(255,255,255,0.035)', color: '#a8a8b3', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              <Sparkles className="h-3 w-3" /> Шинэ цуглуулга
            </div>
            <h3
              className="mt-5 font-semibold tracking-[-0.025em] leading-[0.95] text-white"
              style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(36px, 6vw, 56px)' }}
            >
              Шинэ улирал,<br />шинэ загвар.
            </h3>
            <p className="mt-4 text-[15px] leading-7 max-w-xl mx-auto" style={{ color: 'rgba(255,255,255,0.65)' }}>
              {vendor?.description || `${name}-ийн шилмэл цуглуулгыг өнөөдөр нээж үзээрэй. Таны хэв маягтай таарах зүйлийг олоорой.`}
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <a
                href={navigateUrl('/product', pathname)}
                className="h-12 px-7 rounded-full text-[14px] font-medium inline-flex items-center gap-2"
                style={{ background: 'white', color: '#0a0a0c' }}
              >
                Дэлгүүр үзэх <ArrowRight className="h-3.5 w-3.5" />
              </a>
              {vendor?.modules?.includes('brand') && (
                <a
                  href={navigateUrl('/brand', pathname)}
                  className="h-12 px-7 rounded-full text-[14px] font-medium inline-flex items-center gap-2 transition-colors hover:bg-white/5"
                  style={{ background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.13)' }}
                >
                  Брэндүүд үзэх
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }} />

      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pt-10 pb-10">
        <div className="flex items-center justify-between gap-8 pb-12" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="min-w-0 flex-1 text-center md:text-left" draggable="false">
            {vendor?.logo ? (
              <img
                src={formatImageURL(vendor.logo)}
                alt={name}
                className="max-w-60 w-auto object-contain select-none mx-auto md:mx-0"
                style={{ maxHeight: 'clamp(40px, 5vw, 64px)' }}
              />
            ) : (
              <h2
                className="font-semibold tracking-[-0.04em] leading-[0.85] text-white"
                style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(28px, 3.5vw, 44px)' }}
              >
                {name}
              </h2>
            )}
            {vendor?.description && (
              <p className="mt-6 text-[13px] leading-relaxed max-w-md mx-auto md:mx-0" style={{ color: 'rgba(255,255,255,0.6)' }}>
                {vendor.description}
              </p>
            )}
          </div>
          <div className="hidden md:flex flex-col items-end shrink-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em]" style={{ color: '#a8a8b3' }}>Холбоо барих</div>
            {vendor?.email && <a href={`mailto:${vendor.email}`} className="mt-3 text-[15px] font-medium text-white hover:opacity-80 transition-opacity">{vendor.email}</a>}
            {vendor?.phone && <a href={`tel:${vendor.phone}`} className="mt-1 text-[13px] hover:text-white transition-colors" style={{ color: 'rgba(255,255,255,0.6)' }}>{vendor.phone}</a>}
            {vendor?.address && <div className="mt-1 text-[13px] text-right max-w-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>{vendor.address}</div>}
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-10 pt-12">
          {hasInfoSection && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>Мэдээлэл</div>
              <ul className="mt-5 space-y-3.5">
                {info.map((item: any) => (
                  <li key={item.name}>
                    <a href={navigateUrl(item.href, pathname)} className="text-[14px] hover:text-white transition-colors" style={{ color: 'rgba(255,255,255,0.6)' }}>
                      {item.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>Үйлчилгээ</div>
            <ul className="mt-5 space-y-3.5">
              {help.map((item: any) => (
                <li key={item.name}>
                  <a href={navigateUrl(item.href, pathname)} className="text-[14px] hover:text-white transition-colors" style={{ color: 'rgba(255,255,255,0.6)' }}>
                    {item.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          {(vendor?.phone || vendor?.email || vendor?.address) && (
            <div className="col-span-2 sm:col-span-1 text-center sm:text-left">
              <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>Холбоо</div>
              <ul className="mt-5 space-y-3.5 inline-flex flex-col items-center sm:items-start">
                {vendor?.phone && (
                  <li>
                    <a href={`tel:${vendor.phone}`} className="inline-flex items-center gap-2 text-[14px] hover:text-white transition-colors" style={{ color: 'rgba(255,255,255,0.6)' }}>
                      <Phone className="h-3.5 w-3.5" />
                      {vendor.phone}
                    </a>
                  </li>
                )}
                {vendor?.email && (
                  <li>
                    <a href={`mailto:${vendor.email}`} className="inline-flex items-center gap-2 text-[14px] hover:text-white transition-colors break-all" style={{ color: 'rgba(255,255,255,0.6)' }}>
                      <Mail className="h-3.5 w-3.5 shrink-0" />
                      <span>{vendor.email}</span>
                    </a>
                  </li>
                )}
                {vendor?.address && (
                  <li className="inline-flex items-start gap-2 text-[14px]" style={{ color: 'rgba(255,255,255,0.6)' }}>
                    <MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span>{vendor.address}</span>
                  </li>
                )}
              </ul>
            </div>
          )}
          <div className="col-span-2 sm:col-span-1 text-center sm:text-left flex flex-col items-center sm:items-stretch">
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>
              {socialLinks.length > 0 ? 'Дагах' : 'Баталгаажуулалт'}
            </div>
            {socialLinks.length > 0 && (
              <div className="mt-5 flex gap-2 justify-center sm:justify-start">
                {socialLinks.map((item: any) => (
                  <a key={item.name} href={item.href} target="_blank" rel="noopener noreferrer" aria-label={item.name} className="w-9 h-9 inline-flex items-center justify-center rounded-full transition-colors hover:bg-white/10" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.7)' }}>
                    <item.icon className="h-3.5 w-3.5" />
                  </a>
                ))}
              </div>
            )}
            <a href={navigateUrl('/status', pathname)} className="mt-5 rounded-2xl p-4 flex items-start gap-3 transition-colors hover:bg-white/5 w-full max-w-xs lg:max-w-none text-left" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
              {vendor?.isDanVerified ? (
                <>
                  <div className="w-9 h-9 rounded-full inline-flex items-center justify-center shrink-0" style={{ background: 'rgba(16,185,129,0.14)', border: '1px solid rgba(16,185,129,0.3)', color: '#34d399' }}>
                    <ShieldCheck className="h-4 w-4" strokeWidth={2.5} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold text-white leading-tight">Баталгаажсан</div>
                    <div className="text-[11px] mt-1 leading-snug" style={{ color: 'rgba(255,255,255,0.5)' }}>Улсын бүртгэлээр баталгаажсан</div>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-9 h-9 rounded-full inline-flex items-center justify-center shrink-0" style={{ background: 'rgba(239,68,68,0.14)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5' }}>
                    <ShieldX className="h-4 w-4" strokeWidth={2.5} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold text-white leading-tight">Баталгаажаагүй</div>
                    <div className="text-[11px] mt-1 leading-snug" style={{ color: 'rgba(255,255,255,0.5)' }}>Улсын бүртгэлээр баталгаажаагүй</div>
                  </div>
                </>
              )}
            </a>
          </div>
        </div>
      </div>

      <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 py-5 flex flex-col items-center justify-center gap-2 text-[11px] text-center pb-28 md:flex-row md:justify-between md:text-left md:pb-5" style={{ color: 'rgba(255,255,255,0.4)' }}>
          <div>© {new Date().getFullYear()} {name}. Бүх эрх хуулиар хамгаалагдсан.</div>
          <a href="https://shop.mn" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:text-white transition-colors">
            <span>Онлайн худалдааны шийдэл</span>
            <Image src={ShopmnLogo} alt="Shop.mn" className="h-4 w-auto" />
          </a>
        </div>
      </div>
    </footer>
  )
}
